import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { IoCheckmarkCircleOutline, IoChevronForward } from 'react-icons/io5';
import { useAuth } from '../contexts/AuthContext';
import { readerApi } from '../services/api';
import useChapterLogger from '../hooks/useChapterLogger';
import LogMoment from '../components/log/LogMoment';
import { chapterApi, esvApi } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import ChapterDisplay from '../components/reading/ChapterDisplay';
import ChapterPicker from '../components/reading/ChapterPicker';
import ReadingSettings from '../components/reading/ReadingSettings';
import ReadingToolbar from '../components/reading/ReadingToolbar';
import { getReadingPrefs, setReadingPrefs } from '../utils/localPreferences';
import './ReadingPage.css';

const ReadingPage = () => {
  const [chapters, setChapters] = useState([]);
  const [currentChapter, setCurrentChapter] = useState(null);
  const [chapterText, setChapterText] = useState(null);
  const [loading, setLoading] = useState(true);
  const [textLoading, setTextLoading] = useState(false);
  const [error, setError] = useState(null);
  const [prefs, setPrefs] = useState(getReadingPrefs);
  const [sheet, setSheet] = useState(null); // 'picker' | 'settings' | null
  const pageRef = useRef(null);
  const { user } = useAuth();
  const {
    reader, logChapter, wasLogged, logging,
    moment, momentLabel, recap, clearMoment, undo, saveReflection, hasReports
  } = useChapterLogger({ userId: user?.id });
  const textFormat = prefs.format;
  const esvOptions = useMemo(() => ({
    'include-verse-numbers': prefs.verseNumbers,
    'include-headings': prefs.headings,
    'include-footnotes': prefs.footnotes
  }), [prefs.verseNumbers, prefs.headings, prefs.footnotes]);

  // Load all chapters on component mount
  useEffect(() => {
    loadChapters();
  }, []);

  // Load chapter text when current chapter changes
  useEffect(() => {
    if (currentChapter) {
      loadChapterText();
    }
  }, [currentChapter, textFormat, esvOptions]);

  useEffect(() => {
    setReadingPrefs(prefs);
  }, [prefs]);

  // A new chapter starts at its first verse, not wherever the last one ended.
  // The first chapter shown is already at the top.
  const shownChapterId = useRef(null);
  useEffect(() => {
    if (!currentChapter) return;
    if (shownChapterId.current !== null && shownChapterId.current !== currentChapter.id) {
      pageRef.current?.scrollIntoView({ block: 'start' });
    }
    shownChapterId.current = currentChapter.id;
  }, [currentChapter]);

  const loadChapters = async () => {
    try {
      setLoading(true);
      
      // Since the default API returns paginated results, we need to fetch all chapters
      // We'll request a large limit to get all chapters at once
      const response = await chapterApi.getChapters({ limit: 1200 }); // Ensure we get all 1189 chapters
      
      // The API returns { chapters: [...], pagination: {...} }
      const chaptersData = response.data.chapters;
      
      if (!Array.isArray(chaptersData)) {
        throw new Error('Invalid chapters data received from API');
      }
      
      setChapters(chaptersData);

      // WS5: a claim deep-links here with ?chapter=id. Prefer that over
      // "next chapter" so "Read Nahum 2" actually opens Nahum 2.
      const requestedId = parseInt(new URLSearchParams(window.location.search).get('chapter'), 10);
      let startingChapter = Number.isInteger(requestedId)
        ? chaptersData.find(ch => ch.id === requestedId) || null
        : null;

      // WS7: open where the reader left off, not at Genesis 1 every time.
      if (!startingChapter) {
        try {
          const readersResponse = await readerApi.getReaders();
          const list = readersResponse.data.readers || [];
          const primary = list.find(r => r.isPrimary) || list[0];
          if (primary) {
            const next = await readerApi.getNextChapter(primary.id);
            const nextId = next.data.nextChapter?.chapterId;
            startingChapter = chaptersData.find(ch => ch.id === nextId) || null;
          }
        } catch (nextError) {
          // Not critical — fall back to Genesis 1.
          console.warn('Could not resolve next chapter, starting at Genesis 1:', nextError);
        }
      }

      const firstChapter = startingChapter
        || chaptersData.find(ch => ch.id === 1)
        || chaptersData[0];
      if (firstChapter) {
        setCurrentChapter(firstChapter);
      }
      
      setError(null);
    } catch (err) {
      console.error('Failed to load chapters:', err);
      setError('Failed to load Bible chapters. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadChapterText = async () => {
    if (!currentChapter) return;

    try {
      setTextLoading(true);
      
      const apiMethod = textFormat === 'html' ? esvApi.getChapterHTML : esvApi.getChapterText;
      const response = await apiMethod(currentChapter.id, esvOptions);
      
      setChapterText(response.data.data);
      setError(null);
    } catch (err) {
      console.error('Failed to load chapter text:', err);
      
      let errorMessage = 'Failed to load chapter text. Please try again.';
      
      if (err.response?.data?.errorType) {
        errorMessage = err.response.data.error || errorMessage;
        
        // Add retry information for rate limiting
        if (err.response.data.errorType === 'rate_limit' && err.response.data.retryAfter) {
          errorMessage += ` (Retry in ${err.response.data.retryAfter} seconds)`;
        }
      } else if (err.response?.status === 429) {
        errorMessage = 'ESV API rate limit exceeded. Please try again in a few minutes.';
      } else if (err.response?.status === 408) {
        errorMessage = 'Request timed out. Please check your internet connection.';
      }
      
      setError(errorMessage);
      setChapterText(null);
    } finally {
      setTextLoading(false);
    }
  };

  const handleChapterChange = (chapterId) => {
    const chapter = chapters.find(ch => ch.id === parseInt(chapterId));
    if (chapter) {
      setCurrentChapter(chapter);
    }
  };

  // Wraps at both ends, so a read-through can roll from Revelation 22
  // straight back to Genesis 1.
  const currentIndex = currentChapter ? chapters.findIndex(ch => ch.id === currentChapter.id) : -1;
  const prevChapter = currentIndex >= 0 ? chapters[(currentIndex - 1 + chapters.length) % chapters.length] : null;
  const nextChapter = currentIndex >= 0 ? chapters[(currentIndex + 1) % chapters.length] : null;

  // WS7: tapping Mark as read logs the chapter and then moves on to the
  // next one.
  const advance = useCallback(() => {
    setCurrentChapter(prev => {
      if (!prev || !chapters.length) return prev;
      const index = chapters.findIndex(ch => ch.id === prev.id);
      return index >= 0 && index < chapters.length - 1 ? chapters[index + 1] : prev;
    });
  }, [chapters]);

  const handleMarkAsRead = useCallback(async () => {
    const logged = await logChapter(currentChapter, { recap: true });
    if (logged) advance();
  }, [logChapter, currentChapter, advance]);

  const handlePrefsChange = (patch) => setPrefs(prev => ({ ...prev, ...patch }));

  const closeSheet = useCallback(() => setSheet(null), []);

  if (loading) {
    return (
      <div className="reading-page loading">
        <LoadingSpinner size="large" message="Loading Bible chapters..." />
      </div>
    );
  }

  if (error && !chapters.length) {
    return (
      <div className="reading-page error">
        <div className="error-message">
          <h2>Unable to load the Bible</h2>
          <p>{error}</p>
          <button onClick={loadChapters} className="retry-button">
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!currentChapter) return null;

  return (
    <div className="reading-page" ref={pageRef}>
      <ReadingToolbar
        currentChapter={currentChapter}
        prevChapter={prevChapter}
        nextChapter={nextChapter}
        onPrev={() => setCurrentChapter(prevChapter)}
        onNext={() => setCurrentChapter(nextChapter)}
        onOpenPicker={() => setSheet('picker')}
        onOpenSettings={() => setSheet('settings')}
      />

      <div className="reading-content">
        {textLoading ? (
          <div className="text-loading">
            <LoadingSpinner message="Loading chapter text..." />
          </div>
        ) : error ? (
          <div className="text-error">
            <p>{error}</p>
            <button onClick={loadChapterText} className="retry-button">
              Retry
            </button>
          </div>
        ) : (
          <ChapterDisplay
            chapter={currentChapter}
            chapterText={chapterText}
            format={textFormat}
            fontSize={prefs.fontSize}
          />
        )}
      </div>

      <div className="chapter-end">
        {/* WS7: log what you just read without leaving the page */}
        {reader && (
          <div className="mark-as-read-bar">
            <div className="mark-as-read-copy">
              <strong>Finished {currentChapter.book} {currentChapter.chapter}?</strong>
              <span>Logging it for {reader.name}.</span>
            </div>
            <button
              type="button"
              className={`mark-as-read-button ${wasLogged(currentChapter) ? 'done' : ''}`}
              onClick={handleMarkAsRead}
              disabled={logging || wasLogged(currentChapter)}
            >
              <IoCheckmarkCircleOutline size={18} aria-hidden="true" />
              {wasLogged(currentChapter) ? 'Logged' : logging ? 'Logging…' : 'Mark as read'}
            </button>
          </div>
        )}

        {nextChapter && (
          <button
            type="button"
            className="next-chapter-button"
            onClick={() => setCurrentChapter(nextChapter)}
          >
            Next: {nextChapter.book} {nextChapter.chapter}
            <IoChevronForward size={18} aria-hidden="true" />
          </button>
        )}
      </div>

      <ChapterPicker
        open={sheet === 'picker'}
        onClose={closeSheet}
        chapters={chapters}
        currentChapter={currentChapter}
        onSelect={handleChapterChange}
      />

      <ReadingSettings
        open={sheet === 'settings'}
        onClose={closeSheet}
        prefs={prefs}
        onChange={handlePrefsChange}
      />

      {moment && (
        <LogMoment
          moment={moment}
          recap={recap}
          chapterLabel={momentLabel}
          onClose={clearMoment}
          onUndo={undo}
          onSaveReflection={hasReports ? saveReflection : undefined}
        />
      )}
    </div>
  );
};

export default ReadingPage;
